import { Injectable, computed, inject, signal } from '@angular/core';
import { first } from 'rxjs';

import { NewsService, NewsFeedItem } from '../features/shared/services/news.service';

export interface NewsItem extends NewsFeedItem {
  /** Estado local de UI (expandido/recolhido) — nunca enviado ao backend. */
  expanded: boolean;
}

const PAGE_SIZE = 10;

/**
 * Estado do feed de "Novidades": lista paginada (scroll infinito), filtro de data e
 * contador de não lidas (usado tanto pela página quanto pelo badge da sidebar).
 */
@Injectable({ providedIn: 'root' })
export class NewsStore {
  private readonly newsService = inject(NewsService);

  private readonly _items = signal<NewsItem[]>([]);
  private readonly _loading = signal(false);
  private readonly _hasMore = signal(true);
  private readonly _unreadCount = signal(0);

  private page = 0;
  private fromDate?: string;
  private toDate?: string;
  private title?: string;
  private readStatus?: boolean;
  private loadedOnce = false;

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  /** Carrega a primeira página (reinicia a lista), opcionalmente com filtro de data, título e/ou leitura. */
  loadFirstPage(fromDate?: string, toDate?: string, title?: string, readStatus?: boolean): void {
    this.page = 0;
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.title = title;
    this.readStatus = readStatus;
    this._items.set([]);
    this._hasMore.set(true);
    this.loadedOnce = true;
    this.fetchPage();
  }

  /** Carrega a próxima página e concatena à lista atual (scroll infinito). */
  loadMore(): void {
    if (!this.loadedOnce) {
      this.loadFirstPage();
      return;
    }
    if (this._loading() || !this._hasMore()) {
      return;
    }
    this.page += 1;
    this.fetchPage();
  }

  /**
   * Expande/recolhe um item. Ao expandir pela primeira vez um item ainda não lido,
   * marca como lido (otimista) e avisa o backend.
   */
  toggleExpand(id: number): void {
    const item = this._items().find((i) => i.id === id);
    if (!item) {
      return;
    }

    const willExpand = !item.expanded;
    this._items.update((items) =>
      items.map((i) => (i.id === id ? { ...i, expanded: willExpand } : i)),
    );

    if (willExpand && !item.lida) {
      this.markAsRead(id);
    }
  }

  /** Busca a contagem de não lidas do usuário logado (badge da sidebar). */
  refreshUnreadCount(): void {
    this.newsService
      .getUnreadCount()
      .pipe(first())
      .subscribe({
        next: (result) => this._unreadCount.set(result?.total ?? 0),
        error: () => {
          // Silencia erros de contagem para não atrapalhar a navegação normal.
        },
      });
  }

  private markAsRead(id: number): void {
    // Otimista: marca localmente e decrementa o contador antes da resposta do backend.
    this._items.update((items) => items.map((i) => (i.id === id ? { ...i, lida: true } : i)));
    this._unreadCount.update((count) => Math.max(0, count - 1));

    this.newsService
      .markAsRead(id)
      .pipe(first())
      .subscribe({
        error: () => {
          // Se a chamada falhar, a próxima carga do feed/contador corrige o estado.
        },
      });
  }

  private fetchPage(): void {
    this._loading.set(true);
    const paginaAtual = this.page;

    this.newsService
      .getFeed({
        page: paginaAtual,
        size: PAGE_SIZE,
        fromDate: this.fromDate,
        toDate: this.toDate,
        title: this.title,
        readStatus: this.readStatus,
      })
      .pipe(first())
      .subscribe({
        next: (response) => {
          const novosItens: NewsItem[] = (response.content || []).map((item) => ({
            ...item,
            expanded: false,
          }));
          this._items.update((items) => (paginaAtual === 0 ? novosItens : [...items, ...novosItens]));
          this._hasMore.set(paginaAtual + 1 < (response.totalPages ?? 0));
          this._loading.set(false);
        },
        error: () => {
          this._loading.set(false);
          this._hasMore.set(false);
        },
      });
  }
}
